<script setup lang="ts">
import { computed } from 'vue';

import { AU_PHONE_PREFIX } from '@/utils/PortalConstants';
import { phoneValidationRule } from '@/helpers/validationRules';

import fieldStyles from '@/styles/field.module.scss';

export type FieldSize = 'xs' | 'sm' | 'md' | 'lg';

export interface Props {
  modelValue?: number | string | null;
  size?: FieldSize;
  label?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules?: any[]; // TODO import rules type from vuetify
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'md',
  label: undefined,
  rules: () => [],
});
const emit = defineEmits<{
  'update:modelValue': [value: Exclude<(typeof props)['modelValue'], number>];
}>();

// Writable computed (not useField): the mask sees bare digits while modelValue gains the prefix.
const fieldValue = computed<Props['modelValue']>({
  get() {
    const value = props.modelValue;
    if (typeof value === 'string' && value.startsWith(AU_PHONE_PREFIX)) {
      return value.slice(AU_PHONE_PREFIX.length);
    }
    return value;
  },
  set(value) {
    const prefixed =
      typeof value === 'string' && value ? `${AU_PHONE_PREFIX}${value}` : value;
    emit(
      'update:modelValue',
      prefixed as Exclude<(typeof props)['modelValue'], number>,
    );
  },
});

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-mask-input
    v-model="fieldValue"
    :label="props.label"
    :class="['g-v-text-field', $style.kPhoneField, fieldStyles[sizeClass]]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    data-testid="kPhoneField"
    :prefix="AU_PHONE_PREFIX"
    placeholder="# #### ####"
    mask="# #### ####"
    :rules="[...props.rules, phoneValidationRule]"
  />
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.kPhoneField {
  :global {
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}
</style>
